import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import Select from 'react-select';
import { toast } from 'react-toastify';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 95%;
  max-width: 100%;
  max-height: 85vh;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  color: #2c3e50;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #7f8c8d;
  &:hover { color: #34495e; }
`;

const ModalBody = styled.div`
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionTitle = styled.h3`
  margin: 0.25rem 0 0.5rem 0;
  font-size: 1.05rem;
  color: #2c3e50;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 220px);
  gap: 0.5rem;
  justify-content: center;
`;

const SummaryRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const SummaryCol = styled.div`
  flex: 0 1 720px;
  min-width: 280px;
  min-height: 0;
`;

const Card = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0.5rem;
`;

const CardTitle = styled.div`
  font-size: 0.85rem;
  color: #6c757d;
`;

const CardValue = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #3498db;
`;

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
`;

const Chip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${props => props.$changed ? '#fff0cc' : '#eef2f7'};
  color: ${props => props.$changed ? '#7f4f00' : '#2c3e50'};
  border: 1px solid ${props => props.$changed ? '#ffd27f' : '#dee2e6'};
  font-size: 0.8rem;
  line-height: 1.2;
`;

const Qty = styled.span`
  font-weight: 700;
  font-size: calc(1em + 2px);
`;

const Footer = styled.div`
  padding: 0.75rem 1rem 1rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Left = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Right = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Button = styled.button`
  padding: 0.6rem 1rem;
  border: 1px solid transparent;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  font-size: 0.95rem;
`;

const PrimaryButton = styled(Button)`
  background: ${props => props.disabled ? '#6c757d' : '#28a745'};
  color: white;
  border-color: ${props => props.disabled ? '#6c757d' : '#28a745'};
`;

const SecondaryButton = styled(Button)`
  background: #f8f9fa;
  color: #2c3e50;
  border-color: #dee2e6;
`;

const DangerButton = styled(Button)`
  background: #fff5f5;
  color: #dc3545;
  border-color: #f5c2c7;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
`;

const Th = styled.th`
  background: #f1f3f5;
  text-align: left;
  padding: 0.6rem;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 1px solid #dee2e6;
`;

const Td = styled.td`
  padding: 0.6rem;
  border-bottom: 1px solid #f1f3f5;
  vertical-align: middle;
  font-size: 0.92rem;
`;

const RowChanged = styled.tr`
  background: #fcf8e3;
`;

const selectStyles = {
  container: (provided) => ({
    ...provided,
    minWidth: '220px',
  }),
  control: (provided) => ({
    ...provided,
    fontSize: '0.9rem',
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 1100,
  })
};

function VendorReassignModal({ isOpen, onClose, clientes, vendedores, draft, setDraft, onSave }) {
  const changesByClient = draft?.changesByClient || {};
  const notas = draft?.notas || '';

  // UI state
  const [notasPromptOpen, setNotasPromptOpen] = useState(false);
  const [tempNotas, setTempNotas] = useState(notas || '');
  const [searchText, setSearchText] = useState('');
  const [onlyChanges, setOnlyChanges] = useState(false);
  const [sortBy, setSortBy] = useState(null); // 'codigo' | 'cliente' | 'estado'
  const [sortDir, setSortDir] = useState('asc');

  const vendedoresOptions = useMemo(() => {
    return (vendedores || []).map(v => ({ value: v.ven_codigo, label: `${v.ven_codigo} - ${v.ven_nombre}` }));
  }, [vendedores]);

  const vendorNameById = useMemo(() => {
    const map = new Map();
    (vendedores || []).forEach(v => map.set(v.ven_codigo, v.ven_nombre));
    return map;
  }, [vendedores]);

  // Resumen actual por vendedor
  const resumenActual = useMemo(() => {
    const counts = {};
    (clientes || []).forEach(c => {
      const v = c.clt_ven_codigo;
      if (!v) return;
      counts[v] = (counts[v] || 0) + 1;
    });
    return counts;
  }, [clientes]);

  // Resumen proyectado por vendedor
  const resumenProyectado = useMemo(() => {
    const counts = {};
    (clientes || []).forEach(c => {
      const ch = changesByClient[c.clt_codigo] || {};
      const nuevo = ch.vendedor_nuevo_id || null;
      const destino = nuevo && nuevo !== c.clt_ven_codigo ? nuevo : c.clt_ven_codigo;
      if (!destino) return;
      counts[destino] = (counts[destino] || 0) + 1;
    });
    return counts;
  }, [clientes, changesByClient]);

  const filas = useMemo(() => {
    return (clientes || []).map(c => {
      const ch = changesByClient[c.clt_codigo] || {};
      const vendedorActual = c.clt_ven_codigo;
      const vendedorNuevoId = ch.vendedor_nuevo_id || null;
      const changed = Boolean(vendedorNuevoId && vendedorNuevoId !== vendedorActual);
      return { c, vendedorActual, vendedorNuevoId, changed };
    });
  }, [clientes, changesByClient]);

  const filasFiltradasOrdenadas = useMemo(() => {
    let arr = filas;
    if (searchText && searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      arr = arr.filter(f =>
        String(f.c.clt_codigo).toLowerCase().includes(q) ||
        (String(f.c.clt_nombre || '')).toLowerCase().includes(q) ||
        (String(f.c.clt_ciudad || '')).toLowerCase().includes(q) ||
        (String(f.c.clt_zonadespacho || '')).toLowerCase().includes(q)
      );
    }
    if (onlyChanges) arr = arr.filter(f => f.changed);

    if (sortBy) {
      const dir = sortDir === 'asc' ? 1 : -1;
      arr = [...arr].sort((a, b) => {
        if (sortBy === 'codigo') {
          return String(a.c.clt_codigo).localeCompare(String(b.c.clt_codigo)) * dir;
        }
        if (sortBy === 'cliente') {
          return String(a.c.clt_nombre || '').localeCompare(String(b.c.clt_nombre || '')) * dir;
        }
        if (sortBy === 'estado') {
          const va = a.changed ? 1 : 0;
          const vb = b.changed ? 1 : 0;
          if (va !== vb) return (va - vb) * dir;
          return String(a.c.clt_codigo).localeCompare(String(b.c.clt_codigo)) * dir;
        }
        if (sortBy === 'ciudad') {
          return String(a.c.clt_ciudad || '').localeCompare(String(b.c.clt_ciudad || '')) * dir;
        }
        if (sortBy === 'zonadespacho') {
          return String(a.c.clt_zonadespacho || '').localeCompare(String(b.c.clt_zonadespacho || '')) * dir;
        }
        return 0;
      });
    }
    return arr;
  }, [filas, searchText, onlyChanges, sortBy, sortDir]);

  const totalCambios = useMemo(() => filas.filter(f => f.changed).length, [filas]);

  const toggleSort = (key) => {
    setSortBy(prev => {
      if (prev === key) {
        setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return key;
    });
  };

  const handleChange = (clt_codigo, value, vendedorActual) => {
    const newVal = value;
    setDraft(prev => {
      const current = prev.changesByClient?.[clt_codigo] || {};
      const updated = { ...current };
      // No permitir el mismo vendedor
      if (!newVal || newVal === vendedorActual) {
        delete updated.vendedor_nuevo_id; // limpiar cambio inválido o mismo vendedor
      } else {
        updated.vendedor_nuevo_id = newVal;
      }
      const updatedMap = { ...(prev.changesByClient || {}), [clt_codigo]: updated };
      return { ...prev, changesByClient: updatedMap };
    });
  };

  const performGuardar = async (notesValue) => {
    if (totalCambios === 0) {
      toast.info('No hay cambios para guardar');
      return;
    }
    const detalles = filas
      .filter(f => f.changed)
      .map(f => ({ clt_codigo: f.c.clt_codigo, vendedor_nuevo_id: f.vendedorNuevoId || changesByClient[f.c.clt_codigo]?.vendedor_nuevo_id }))
      .filter(x => x.vendedor_nuevo_id && x.vendedor_nuevo_id !== (clientes.find(c => c.clt_codigo === x.clt_codigo)?.clt_ven_codigo));

    try {
      await onSave(detalles, notesValue);
    } catch (e) {
      // onSave maneja toasts
    }
  };

  const abrirNotas = () => {
    setTempNotas(notas || '');
    setNotasPromptOpen(true);
  };
  const confirmarGuardar = async () => {
    await performGuardar(tempNotas);
    setNotasPromptOpen(false);
  };
  const guardarSinNotas = async () => {
    await performGuardar('');
    setNotasPromptOpen(false);
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Reasignación de vendedor</ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        <ModalBody>
          <SummaryRow>
            <SummaryCol>
              <div>
                <SectionTitle>Resumen actual</SectionTitle>
                <Grid>
                  <Card>
                    <CardTitle>Total clientes</CardTitle>
                    <CardValue>{clientes?.length || 0}</CardValue>
                  </Card>
                  <Card>
                    <CardTitle>Por vendedor</CardTitle>
                    <ChipList>
                      {Object.entries(resumenActual).map(([ven, qty]) => (
                        <Chip key={`act-${ven}`}>{ven} - {vendorNameById.get(ven) || ''} <Qty>{qty}</Qty></Chip>
                      ))}
                    </ChipList>
                  </Card>
                </Grid>
              </div>
            </SummaryCol>

            <SummaryCol>
              <div>
                <SectionTitle>Resumen proyectado</SectionTitle>
                <Grid>
                  <Card>
                    <CardTitle>Total clientes</CardTitle>
                    <CardValue>{clientes?.length || 0}</CardValue>
                  </Card>
                  <Card>
                    <CardTitle>Por vendedor</CardTitle>
                    <ChipList>
                      {Array.from(new Set([...Object.keys(resumenActual), ...Object.keys(resumenProyectado)])).map(ven => {
                        const a = resumenActual[ven] || 0;
                        const p = resumenProyectado[ven] || 0;
                        if (a === 0 && p === 0) return null;
                        return (
                          <Chip key={`proj-${ven}`} $changed={a !== p}>
                            {ven} - {vendorNameById.get(ven) || ''} <Qty>{p}</Qty>
                          </Chip>
                        );
                      })}
                    </ChipList>
                  </Card>
                </Grid>
              </div>
            </SummaryCol>
          </SummaryRow>

          <div>
            <SectionTitle>Detalle</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <input
                type="text"
                placeholder="Buscar por código o nombre"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ padding: '0.4rem 0.6rem', border: '1px solid #ced4da', borderRadius: 6, minWidth: 220 }}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={onlyChanges} onChange={(e) => setOnlyChanges(e.target.checked)} />
                Solo cambios
              </label>
            </div>

            <Table>
              <thead>
                <tr>
                  <Th onClick={() => toggleSort('codigo')} style={{ cursor: 'pointer', userSelect: 'none' }}>Código {sortBy === 'codigo' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</Th>
                  <Th onClick={() => toggleSort('cliente')} style={{ cursor: 'pointer', userSelect: 'none' }}>Cliente {sortBy === 'cliente' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</Th>
                  <Th onClick={() => toggleSort('ciudad')} style={{ cursor: 'pointer', userSelect: 'none' }}>Ciudad {sortBy === 'ciudad' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</Th>
                  <Th onClick={() => toggleSort('zonadespacho')} style={{ cursor: 'pointer', userSelect: 'none' }}>Zona Despacho {sortBy === 'zonadespacho' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</Th>
                  <Th>Vendedor actual</Th>
                  <Th>Nuevo vendedor</Th>
                  <Th onClick={() => toggleSort('estado')} style={{ cursor: 'pointer', userSelect: 'none' }}>Estado {sortBy === 'estado' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</Th>
                </tr>
              </thead>
              <tbody>
                {filasFiltradasOrdenadas.map((f) => {
                  const TrComp = f.changed ? RowChanged : 'tr';
                  const options = vendedoresOptions.filter(o => o.value !== f.vendedorActual); // excluir mismo vendedor
                  const valueOpt = f.vendedorNuevoId ? vendedoresOptions.find(o => o.value === f.vendedorNuevoId) : null;
                  return (
                    <TrComp key={f.c.clt_codigo}>
                      <Td>{f.c.clt_codigo}</Td>
                      <Td>{f.c.clt_nombre}</Td>
                      <Td>{f.c.clt_ciudad || '-'}</Td>
                      <Td>{f.c.clt_zonadespacho || '-'}</Td>
                      <Td>{f.vendedorActual} - {vendorNameById.get(f.vendedorActual) || ''}</Td>
                      <Td>
                        <Select
                          styles={selectStyles}
                          options={options}
                          value={valueOpt}
                          onChange={(opt) => handleChange(f.c.clt_codigo, opt?.value || null, f.vendedorActual)}
                          isClearable
                          menuPortalTarget={document.body}
                          placeholder="Seleccionar vendedor"
                        />
                      </Td>
                      <Td>
                        {f.changed ? 'Cambio' : 'Sin cambio'}
                      </Td>
                    </TrComp>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </ModalBody>
        <Footer>
          <Left>
            <SecondaryButton onClick={onClose}>Cerrar</SecondaryButton>
            <DangerButton onClick={() => setDraft({ changesByClient: {}, notas: '' })}>Descartar borrador</DangerButton>
          </Left>
          <Right>
            {notasPromptOpen ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <textarea
                  value={tempNotas}
                  onChange={(e) => setTempNotas(e.target.value)}
                  placeholder="Notas (opcional)"
                  style={{ minWidth: 280, minHeight: 44, padding: '0.5rem', borderRadius: 6, border: '1px solid #ced4da' }}
                />
                <PrimaryButton onClick={confirmarGuardar} disabled={totalCambios === 0}>
                  Confirmar guardado
                </PrimaryButton>
                <SecondaryButton onClick={guardarSinNotas} disabled={totalCambios === 0}>
                  Guardar sin notas
                </SecondaryButton>
                <SecondaryButton onClick={() => setNotasPromptOpen(false)}>Cancelar</SecondaryButton>
              </div>
            ) : (
              <PrimaryButton onClick={abrirNotas} disabled={totalCambios === 0}>
                {totalCambios > 0 ? `Guardar cambios (${totalCambios})` : 'Guardar cambios'}
              </PrimaryButton>
            )}
          </Right>
        </Footer>
      </ModalContent>
    </ModalOverlay>
  );
}

export default VendorReassignModal;
